import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type User } from '../../types';
import { useUserPageHref } from './user-page-href';
import { UserResultRow } from './UserResultRow';
import { UserSearchCombobox } from './UserSearchCombobox';

const RECENT_KEY = 'tronche2tek:recentUsers';
const RECENT_MAX = 5;

type RecentEntry = {
  id: string;
  displayName: string;
  userPrincipalName: string;
};

function loadRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (e: unknown): e is RecentEntry =>
        typeof e === 'object' &&
        e !== null &&
        'id' in e &&
        typeof e.id === 'string' &&
        'displayName' in e &&
        typeof e.displayName === 'string' &&
        'userPrincipalName' in e &&
        typeof e.userPrincipalName === 'string',
    );
  } catch {
    return [];
  }
}

// Takes the bare recent shape rather than a User, so a row already in the
// list can be pushed back to the front without refetching the full user.
function pushRecent({ id, displayName, userPrincipalName }: RecentEntry) {
  const entry: RecentEntry = { id, displayName, userPrincipalName };
  const existing = loadRecent().filter((e) => e.id !== entry.id);
  const next = [entry, ...existing].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage full or denied — ignore
  }
}

export function UserSearchTopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<RecentEntry[]>(() => loadRecent());
  const inputRef = useRef<HTMLInputElement>(null);
  const userPageHref = useUserPageHref();

  // Global / shortcut to focus
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        const { activeElement } = document;
        const tag =
          activeElement instanceof HTMLElement ? activeElement.tagName : null;
        if (tag === 'INPUT' || tag === 'TEXTAREA') {
          return;
        }
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function pick(user: User) {
    remember(user);
    setQuery('');
    void navigate({ to: '/users/$oid', params: { oid: user.id } });
  }

  // A person opened in a new tab has been visited just as much as one picked
  // in place, so they earn their spot in the recents either way.
  function remember(entry: RecentEntry) {
    pushRecent(entry);
    setRecent(loadRecent());
  }

  function pickRecent(entry: RecentEntry, close: () => void) {
    setQuery('');
    close();
    void navigate({ to: '/users/$oid', params: { oid: entry.id } });
  }

  return (
    <UserSearchCombobox
      value={query}
      onValueChange={setQuery}
      onSelect={pick}
      placeholder={t('topSearch.placeholder', 'Chercher un utilisateur…')}
      className="w-full"
      inputClassName="w-full bg-transparent text-sm py-2 px-3 focus:outline-none"
      inputStyle={{
        border: '1px solid rgba(242,244,248,0.25)',
        color: 'var(--inverse-fg)',
        caretColor: 'var(--green)',
      }}
      inputRef={inputRef}
      getItemHref={(user) => userPageHref(user.id)}
      onOpenInNewTab={remember}
      clearOnEscape
      renderTrailing={(loading) => (
        <span className="mono pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 border border-inverse-fg/20 px-1.5 py-0.5 text-[10px] text-inverse-fg/50">
          {loading ? '…' : '/'}
        </span>
      )}
      renderEmptyQuery={(close) => {
        if (recent.length === 0) {
          return null;
        }
        return (
          <div className="absolute inset-x-0 z-50 mt-1 border border-ink bg-card text-ink">
            <p className="label dash border-b border-b-rule px-3 py-2 text-muted">
              {t('topSearch.recent', 'Récents')}
            </p>
            <ul>
              {recent.map((entry) => (
                <UserResultRow
                  key={entry.id}
                  oid={entry.id}
                  displayName={entry.displayName}
                  userPrincipalName={entry.userPrincipalName}
                  href={userPageHref(entry.id)}
                  onSelect={() => pickRecent(entry, close)}
                  onOpenInNewTab={() => remember(entry)}
                />
              ))}
            </ul>
          </div>
        );
      }}
    />
  );
}
